import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getImage } from '../../../apis/mediaAPI';
import PersonalAlarmImg from '../../../assets/icons/siren--red.svg';
import PlaceholderMap from '../../../assets/imgs/chats/map-placeholder2.png';
import { ReactComponent as TriangleError } from '../../../assets/imgs/chats/triangle-error.svg';
import { ReactComponent as MapMarker } from '../../../assets/icons/map-marker-gray.svg';
import { useLocation } from '../../../utils/customHooks/useLocation';
import { MessageSubType, MessageType } from '../../../utils/enums';
import { translate } from '../../../utils/translate';
import HyperLinkText from '../../HyperlinkText/HyperLinkText';
import { MapMessageView, SMapMessageView } from '../../MapMessageView/MapMessageView';
import MessageContainer from '../../MessageContainer/MessageContainer';
import type { PrewrittenTextLanguage } from '../../../containers/ChatsList/chatListSlice/types';
import {
  SMapWrapper,
  SAlarmText,
  SAlertContainer,
  SAlertMessageInfoContainer,
  SNoMapContainer,
  SPrewrittenTextTitleText,
  SPrewrittenTextWrapper,
  STime,
  SCustomTextWrapperTime,
  SLocationWrapper,
} from './AlertMessage.styles';
import i18n from '../../../i18n';

type AlertMessageProps = {
  isSent: boolean;
  sentDateTime: string;
  message: any;
  senderName: string;
  senderImage: string | null;
  messageTo: string | null;
  toggleModal?: () => void;
  messageText?: string;
  onMapClick?: (
    longitude: number,
    latitude: number,
    accuracy: number | undefined,
    address: string
  ) => void;
  forwarded?: boolean;
  id?: number;
  didFetchPhoto?: () => void;
};

export const AlertMessage = (props: AlertMessageProps) => {
  const {
    message,
    isSent,
    sentDateTime,
    senderImage,
    senderName,
    messageTo,
    toggleModal,
    forwarded,
    onMapClick,
    id,
    didFetchPhoto,
  } = props;
  const { location, isLocationLoading } = useLocation(message.locationID);
  const [image, setImage] = useState('');
  const { t } = useTranslation();

  const isPersonalAlarm = message?.subType === MessageSubType.personalAlarm;

  const getAlarmImage = async () => {
    if (message?.emergencyTypeIconFileName) {
      const img = await getImage({
        imageName: message?.emergencyTypeIconFileName,
        svg: true,
      });
      setImage(img);
    }
    if (isPersonalAlarm) setImage(PersonalAlarmImg);
  };

  useEffect(() => {
    getAlarmImage();
  }, [message?.id]);

  const messageType =
    isSent && !message.recalled
      ? MessageType.SentAlarmMessage
      : !isSent && !message.recalled
        ? MessageType.ReceivedAlarmMessage
        : isSent && message.recalled
          ? MessageType.SentAlarmRecalledMessage
          : MessageType.ReceivedRecalledAlarmMessage;

  const alarmCustomText = message.text?.trim();
  const hasAlarmCustomText = !!alarmCustomText?.length;
  const hasInstructions = !!message?.emergencyTypePrewrittenTexts?.length;
  const hasAlarmInfo = hasAlarmCustomText || hasInstructions;

  const getDefaultLanguageInstructions = (
    emergencyTypePrewrittenTexts: PrewrittenTextLanguage[]
  ): string | undefined => {
    return emergencyTypePrewrittenTexts.find(
      (element: PrewrittenTextLanguage) => element.Language === i18n.language
    )?.Text;
  };

  const getInstructions = (): string => {
    if (!message?.emergencyTypePrewrittenTexts?.length) return '';

    const { emergencyTypePrewrittenTexts, emergencyTypePrewrittenText } = message;

    return (
      emergencyTypePrewrittenText ||
      getDefaultLanguageInstructions(emergencyTypePrewrittenTexts) ||
      emergencyTypePrewrittenTexts[0].Text
    );
  };

  const instructions = getInstructions();

  return (
    <>
      <MessageContainer
        messageType={messageType}
        senderImage={senderImage}
        senderName={senderName}
        messageTo={forwarded ? null : messageTo}
        toggleModal={toggleModal}
        alert={forwarded ? undefined : !message.recalled}
        forwarded={forwarded}
        id={id}
        hasAlarmInfo={hasAlarmInfo}
      >
        <SAlertContainer isSent={isSent} role="alertMessageInnerContainer">
          <SAlertMessageInfoContainer role="infoContainer">
            <div className="alertDetailsMessageContainer" role="detailsContainer">
              {image && (
                <div className="alertIconContainer" role="iconContainer">
                  <img width={24} src={image} alt="" role="icon" />
                </div>
              )}
              <div className="text-container" role="textContainer">
                <p className="emergencyType" role="emergencyTypeText">
                  {isPersonalAlarm ? t('alarm_personal_alarm') : message.emergencyTypeName}
                </p>
                {message.Organization && message.Organization.name && (
                  <p
                    className={forwarded ? 'forwardedAlarmOrganization' : 'organization'}
                    role="organizationText"
                  >
                    {translate(forwarded ? 'alarm_forwarded_sent_from' : 'sent_from')}{' '}
                    {message.Organization.name}
                  </p>
                )}
              </div>
            </div>
          </SAlertMessageInfoContainer>
        </SAlertContainer>
        <SMapWrapper>
          {!isLocationLoading && location ? (
            <MapMessageView
              didFetchPhoto={didFetchPhoto}
              longitude={Number(location?.longitude)}
              latitude={Number(location?.latitude)}
              accuracy={location.accuracy}
              isSameSender={false}
              isSent={isSent}
              gestureHandling="none"
              onClick={
                onMapClick &&
                (() =>
                  onMapClick(
                    Number(location?.longitude),
                    Number(location?.latitude),
                    location.accuracy ?? undefined,
                    location.name!
                  ))
              }
            />
          ) : (
            <SMapMessageView role="mapView">
              <SNoMapContainer role="noMapContainer">
                <div role="textContainer">
                  <TriangleError role="icon" />
                  <p role="noLocationText">{translate('messages_alarm_no_location')}</p>
                </div>
                <img src={PlaceholderMap} alt="" role="mapPlaceholder" />
              </SNoMapContainer>
            </SMapMessageView>
          )}
          {!hasAlarmInfo && <STime>{sentDateTime}</STime>}
        </SMapWrapper>
        {!isLocationLoading && location?.name && (
          <SLocationWrapper>
            <MapMarker className="mapMarker" />
            <p className={forwarded ? 'forwardedAlarmLocation' : 'location'} role="locationText">
              {location.name}
              {forwarded ? `, ${sentDateTime}` : ''}
            </p>
          </SLocationWrapper>
        )}
        {!!instructions && (
          <SPrewrittenTextWrapper role="prewrittenTextContainer">
            <SPrewrittenTextTitleText role="title">
              {translate('alarm_instructions')}
            </SPrewrittenTextTitleText>
            <SAlarmText role="alarmText">
              <HyperLinkText text={instructions} />
            </SAlarmText>
          </SPrewrittenTextWrapper>
        )}
        {hasAlarmCustomText && (
          <SPrewrittenTextWrapper role="customTextContainer">
            <SPrewrittenTextTitleText role="title">
              {translate('alarm_details')}
            </SPrewrittenTextTitleText>
            <SAlarmText role="alarmText">
              <HyperLinkText text={alarmCustomText} />
            </SAlarmText>
          </SPrewrittenTextWrapper>
        )}
        {hasAlarmInfo && <SCustomTextWrapperTime>{sentDateTime}</SCustomTextWrapperTime>}
      </MessageContainer>
    </>
  );
};
